import { Box, Grid } from '@mui/material';
import { TypeRegister } from 'practice/constants/type-register';
import { memo, useMemo } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import { StepButton, StepTypography } from 'shell/components/shared/step-button';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { noop } from 'shell/utils/noop';
import { PracticeDraftStep, TypeInitiative } from 'practice/constants/practice-management-step';

interface ButtonsProps {
  onCancel: () => void;
  handleExit: () => void;
  disabled?: boolean;
  handleStep?: (id: number) => void;
  activeStep?: number;
  maxActiveStep?: number;
  tipoIniziativa?: string;
  codOperatorePratica?: string;
  onPrintFolder: () => void;
  onPrintReceipt: () => void;
  withEccezioni?: boolean;
  disabledAll?: boolean;
  typeRegister?: string;
  onConfirm: () => void;
  ForPracticeTemporary?: boolean;
  isAcceptMessi?: boolean;
  isDisabledCancel?: boolean;
  statoPratica?: string;
  onPrintLetter: () => void;
  fromPerson?: string;
  highlightConfirm?: boolean;
}

const ButtonsComponent = ({
  onCancel,
  handleExit,
  disabled,
  handleStep = noop,
  activeStep = PracticeDraftStep.RegisterOffice,
  maxActiveStep = PracticeDraftStep.RegisterOffice,
  tipoIniziativa,
  onPrintFolder,
  onPrintReceipt,
  codOperatorePratica,
  withEccezioni,
  disabledAll,
  typeRegister,
  onConfirm,
  ForPracticeTemporary = false,
  isAcceptMessi = false,
  isDisabledCancel = false,
  statoPratica,
  onPrintLetter,
  fromPerson,
  highlightConfirm = false,
}: ButtonsProps): React.ReactElement => {
  const isPraticeUnAvailability: boolean = useMemo(() => {
    return typeRegister === TypeRegister.Unavailable;
  }, [typeRegister]);

  const steps: Array<{
    label: string;
    id: number;
  }> = useMemo(() => {
    if (ForPracticeTemporary) {
      return [
        {
          label: 'Anagrafe',
          id: PracticeDraftStep.RegisterOffice,
        },
        {
          label: 'Famiglia',
          id: PracticeDraftStep.Family,
        },
        {
          label: 'Residenza',
          id: PracticeDraftStep.Residence,
        },
        {
          label: 'Documenti',
          id: PracticeDraftStep.Documents,
        },
      ];
    }

    return [
      {
        label: 'Anagrafe',
        id: PracticeDraftStep.RegisterOffice,
      },
      {
        label: 'Famiglia',
        id: PracticeDraftStep.Family,
      },
      {
        label: 'Residenza',
        id: PracticeDraftStep.Residence,
      },
      {
        label: 'Documenti',
        id: PracticeDraftStep.Documents,
      },
    ];
  }, [isPraticeUnAvailability, ForPracticeTemporary, typeRegister]);

  const disabledConfirm = useMemo(() => {
    if (
      tipoIniziativa === TypeInitiative.Anpr ||
      tipoIniziativa === TypeInitiative.ElixForm ||
      codOperatorePratica === 'BATCH'
    ) {
      return false;
    } // in questi casi è sempre abilitato

    if (typeRegister === TypeRegister.Emigration || ForPracticeTemporary) {
      return maxActiveStep < PracticeDraftStep.AccertamentoMessi;
    }

    return maxActiveStep < PracticeDraftStep.ExceptionList;
  }, [isAcceptMessi, typeRegister, ForPracticeTemporary, maxActiveStep]);

  const activeConfirm = useMemo(() => {
    return (
      isAcceptMessi &&
      (tipoIniziativa === TypeInitiative.Anpr || tipoIniziativa === TypeInitiative.ElixForm)
    );
  }, [isAcceptMessi, tipoIniziativa]);

  const activeRicevuta = useMemo(() => {
    return (
      isAcceptMessi &&
      !(tipoIniziativa === TypeInitiative.Anpr || tipoIniziativa === TypeInitiative.ElixForm)
    );
  }, [isAcceptMessi, tipoIniziativa]);

  return (
    <StickyDiv>
      <Grid container spacing={1}>
        {steps.map((step, index) => (
          <Grid item xs={12} key={step.label?.toString()}>
            <StepButton
              active={
                activeStep === PracticeDraftStep.Documents &&
                isPraticeUnAvailability &&
                step?.id === PracticeDraftStep.Documents
                  ? true
                  : step?.id === activeStep
              }
              disabled={disabled || index > maxActiveStep}
              variant="outlined"
              color="cdmBrandThird"
              fullWidth
              onClick={() => handleStep(step.id)}
            >
              <StepTypography
                active={
                  activeStep === PracticeDraftStep.Documents &&
                  isPraticeUnAvailability &&
                  step?.id === PracticeDraftStep.Documents
                    ? true
                    : step?.id === activeStep
                }
                disabled={disabled || index > maxActiveStep}
              >
                {step.label}
              </StepTypography>
            </StepButton>
          </Grid>
        ))}
      </Grid>
      <div>
        <div className="pmi-pt-4">
          <div className="pmi-pb-2">
            <StepButton
              variant="outlined"
              color="cdmBrandThird"
              fullWidth
              disabled={
                disabled ||
                maxActiveStep <= PracticeDraftStep.AccertamentoMessi ||
                typeRegister === TypeRegister.Emigration ||
                ForPracticeTemporary
              }
              active={PracticeDraftStep.AccertamentoMessi === activeStep}
              onClick={() => handleStep(PracticeDraftStep.AccertamentoMessi)}
            >
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                <StepTypography
                  active={PracticeDraftStep.AccertamentoMessi === activeStep}
                  disabled={
                    disabled ||
                    maxActiveStep <= PracticeDraftStep.AccertamentoMessi ||
                    typeRegister === TypeRegister.Emigration ||
                    ForPracticeTemporary
                  }
                >
                  Accertamento Messi
                </StepTypography>
              </Box>
            </StepButton>
          </div>
          <div className="pmi-pb-2">
            <StepButton
              variant="outlined"
              color="cdmBrandThird"
              fullWidth
              disabled={disabled || maxActiveStep < PracticeDraftStep.Recap}
              onClick={() => handleStep(PracticeDraftStep.Recap)}
              active={PracticeDraftStep.Recap === activeStep}
            >
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                <StepTypography
                  active={PracticeDraftStep.Recap === activeStep}
                  disabled={disabled || maxActiveStep < PracticeDraftStep.Recap}
                >
                  {typeRegister === TypeRegister.Immigration ||
                  typeRegister === TypeRegister.Residence
                    ? 'Domanda di apertura'
                    : 'Riepilogo'}
                </StepTypography>
              </Box>
            </StepButton>
          </div>
          <div className="pmi-pb-2">
            <StepButton
              variant="outlined"
              color="cdmBrandThird"
              fullWidth
              isButton
              onClick={() => onPrintReceipt()}
              disabled
              active={activeRicevuta}
            >
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                <StepTypography disabled active={activeRicevuta}>
                  Stampa Ricevuta
                </StepTypography>
              </Box>
            </StepButton>
          </div>
          {ForPracticeTemporary ? (
            <div className="pmi-pb-2">
              <StepButton
                variant="outlined"
                color="cdmBrandThird"
                fullWidth
                isButton
                onClick={() => onPrintLetter()}
                disabled={maxActiveStep < PracticeDraftStep.AccertamentoMessi}
              >
                <Box
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <StepTypography disabled={maxActiveStep < PracticeDraftStep.AccertamentoMessi}>
                    Stampa Lettera
                  </StepTypography>
                </Box>
              </StepButton>
            </div>
          ) : undefined}
          {ForPracticeTemporary ? undefined : (
            <div className="pmi-pb-2">
              <StepButton
                variant="outlined"
                color="cdmBrandThird"
                fullWidth
                isButton
                onClick={() => onPrintFolder()}
                disabled={
                  maxActiveStep < PracticeDraftStep.AccertamentoMessi ||
                  (fromPerson === TypeInitiative.idUfficio &&
                    typeRegister === TypeRegister.Emigration)
                }
              >
                <Box
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <StepTypography
                    disabled={
                      maxActiveStep < PracticeDraftStep.AccertamentoMessi ||
                      (fromPerson === TypeInitiative.idUfficio &&
                        typeRegister === TypeRegister.Emigration)
                    }
                  >
                    Stampa Cartellina
                  </StepTypography>
                </Box>
              </StepButton>
            </div>
          )}
          <div className="pmi-pb-2">
            <StepButton
              variant="outlined"
              color="cdmBrandThird"
              fullWidth
              isButton
              onClick={() => onConfirm()}
              disabled={disabledConfirm}
              active={activeConfirm || highlightConfirm}
            >
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                <StepTypography
                  disabled={disabledConfirm}
                  active={activeConfirm || highlightConfirm}
                >
                  Conferma
                </StepTypography>
              </Box>
            </StepButton>
          </div>
          {tipoIniziativa === TypeInitiative.Anpr || codOperatorePratica === 'BATCH' ? (
            <div className="pmi-pb-2">
              <StepButton
                variant="outlined"
                color="cdmBrandThird"
                fullWidth
                disabled={
                  disabled ||
                  maxActiveStep < PracticeDraftStep.Irricevibilita ||
                  isPraticeUnAvailability ||
                  typeRegister === TypeRegister.Emigration
                }
                onClick={() => handleStep(PracticeDraftStep.Irricevibilita)}
                active={PracticeDraftStep.Irricevibilita === activeStep}
              >
                <Box
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <StepTypography
                    active={PracticeDraftStep.Irricevibilita === activeStep}
                    disabled={
                      disabled ||
                      maxActiveStep < PracticeDraftStep.Irricevibilita ||
                      isPraticeUnAvailability ||
                      typeRegister === TypeRegister.Emigration
                    }
                  >
                    Pratica Irricevibile
                  </StepTypography>
                </Box>
              </StepButton>
            </div>
          ) : undefined}
        </div>
        <div className="pmi-pb-2">
          <StepButton
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            disabled={disabled || !withEccezioni}
            onClick={() => handleStep(PracticeDraftStep.ExceptionList)}
            active={PracticeDraftStep.ExceptionList === activeStep}
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <StepTypography
                active={PracticeDraftStep.ExceptionList === activeStep}
                disabled={disabled || !withEccezioni}
              >
                Eccezioni
              </StepTypography>
            </Box>
          </StepButton>
        </div>
        <div className="pmi-pb-2">
          <StepButton
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            disabled={
              disabled ||
              // maxActiveStep < 6 ||
              !(isDisabledCancel && statoPratica === '0')
            }
            onClick={() => handleStep(PracticeDraftStep.Annullament)}
            active={PracticeDraftStep.Annullament === activeStep}
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <StepTypography
                active={PracticeDraftStep.Annullament === activeStep}
                disabled={
                  disabled ||
                  // maxActiveStep < 6 ||
                  !(isDisabledCancel && statoPratica === '0')
                }
              >
                Annullamento pratica
              </StepTypography>
            </Box>
          </StepButton>
        </div>
        <div className="pmi-pt-3">
          {disabledAll ||
          tipoIniziativa === TypeInitiative.Anpr ||
          tipoIniziativa === TypeInitiative.ElixForm ? (
            <PmiButton variant="outlined" color="cdmBrand" fullWidth onClick={handleExit}>
              Esci
            </PmiButton>
          ) : (
            <PmiButton
              variant="outlined"
              color="cdmBrand"
              disabled={isDisabledCancel}
              fullWidth
              onClick={onCancel}
            >
              Annulla Operazione
            </PmiButton>
          )}
        </div>
      </div>
    </StickyDiv>
  );
};

ButtonsComponent.displayName = 'Practice Edit - Buttons';

const Buttons = memo(ButtonsComponent);

export default Buttons;
